import { useCallback, useContext, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { AppContext } from "components/App/AppContext";
import { useHistory } from "react-router-dom";
import { JsonForm } from "components/JsonForm";
import { IconInputWidget } from "components/JsonForm/theme/widgets/IconInputWidget";
import { SubmitButton } from "components/Button/SubmitButton";
import { userForgotPassword } from "components/utils";
import { Container } from "components/Layout/Container";
import { PageLink } from "components/App/utils";
import { usePageTopAndBottomRows } from "components/utils/page";
import { PageComponentsList } from ".";
import { ErrorSummary } from "components/ErrorSummary";
import { Icon } from "components/Icon";
import { EmailButton } from "components/Button/EmailButton";
import { Heading } from "../Heading";
import { HeadingLevel } from "../../types/HeadingLevel";

export function ForgotPasswordPage() {
    const history = useHistory();
    const submitted = history?.location?.state?.submitted === true;
    const { programNumber } = useContext(AppContext);

    const { search } = useLocation();
    const routeParams = useMemo(() => new URLSearchParams(search), [search]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { topRows, bottomRows } = usePageTopAndBottomRows();
    const [error, setError] = useState();
    const [email, setEmail] = useState();

    const schema = {
        type: "object",
        required: ["userName"],
        properties: {
            userName: {
                type: "string",
                title: "Email Address",
                format: "email",
                default: email ?? routeParams.get("email") ?? undefined,
            },
        },
    };

    const uiSchema = {
        userName: {
            "ui:widget": "iconInput",
            "ui:options": { icon: "envelope", inputType: "email", helpClassName: "d-block mt-2" },
            "ui:placeholder": "email@example.com",
            "ui:help": "We will send you a secure link to reset your password",
            "ui:autofocus": true,
        },
    };

    const widgets = { iconInput: IconInputWidget };

    const transformErrors = (errors) =>
        errors.map((e) =>
            e.name === "required" && e.property === ".userName" ? { ...e, message: "Enter an email address to continue." } : e
        );

    const onSubmit = useCallback(
        (data) => {
            const { formData } = data;
            setIsSubmitting(true);
            userForgotPassword({
                userName: formData.userName,
                programNumber: programNumber,
            })
                .then(() => history.replace({ pathname: PageLink.ForgotPassword, state: { submitted: true } }))
                .catch((err) => {
                    setError(err);
                })
                .finally(() => {
                    setEmail(formData.userName);
                    setIsSubmitting(false);
                });
        },
        [history, programNumber]
    );

    const onEditEmail = useCallback(() => {
        setError(undefined);
        history.replace({ pathname: PageLink.ForgotPassword, state: { submitted: false } });
    }, [history]);

    return (
        <>
            <PageComponentsList components={topRows} />
            <Container className="p-0">
                <div className="d-flex justify-content-center w-100">
                    <div className="bg-white d-flex flex-column gap-4 p-4 w-100 rounded-5p5 shadow-card" style={{ maxWidth: 480 }}>
                        <div className="d-flex">
                            <Link
                                to={PageLink.SignIn}
                                className="d-inline-flex align-items-center gap-2 rounded text-decoration-none text-portal-primary fs-6 py-1 px-2 ms-n2"
                            >
                                <Icon icon={["fal", "arrow-left"]} />
                                Back to Sign In
                            </Link>
                        </div>
                        <Container className="p-0 d-flex flex-column align-items-center">
                            <Icon icon={["far", "shield-keyhole"]} className="fs-1 text-secondary" />
                            <Heading level={HeadingLevel.H5} className="fw-medium m-0 mt-2">
                                Forgot password?
                            </Heading>
                            <p className="text-secondary mt-1 text-center">Please provide the email associated with your account.</p>
                        </Container>
                        {submitted && email ? (
                            <div className="d-flex flex-column align-items-center gap-2 pb-4">
                                <div>We sent a recovery link to:</div>
                                <EmailButton email={email} onClick={onEditEmail} />
                                <div className="text-secondary fs-7 mt-1">If you haven't received the email, check your spam folder</div>
                            </div>
                        ) : (
                            <>
                                {error && <ErrorSummary errorSummary={error} />}
                                <JsonForm
                                    schema={schema}
                                    uiSchema={uiSchema}
                                    widgets={widgets}
                                    onSubmit={onSubmit}
                                    isSubmitting={isSubmitting}
                                    formName="Forgot Password"
                                    buttonClassName="mt-3"
                                    transformErrors={transformErrors}
                                >
                                    <SubmitButton
                                        type="submit"
                                        variant="primary"
                                        className="w-100"
                                        isSubmitting={isSubmitting}
                                        spinnerText="Loading..."
                                    >
                                        Submit
                                    </SubmitButton>
                                </JsonForm>
                            </>
                        )}
                    </div>
                </div>
            </Container>
            <PageComponentsList components={bottomRows} />
        </>
    );
}
